import { isString } from 'lodash';
import { isCarrierUser } from '@nova/core';
import routeHelpers from '@satellite/helpers/routes';
import {
  buildLoginQueryWithMcpTransaction,
  completeMcpPortalLogin,
  readMcpTransaction,
  withMcpTransactionQuery
} from '@satellite/helpers/mcp-login-bridge';
import companyService from '@satellite/services/company-service';
import { parseQuery } from 'vue-router';
import { useStore } from 'vuex';
import config from '@/config/config';

/**
 * Only allow same-origin relative paths, query and hash included. Parsing with `URL` is what
 * catches the tricks a prefix check misses: `/\evil.com`, and tab or newline separators the URL
 * parser strips before resolving.
 * @param {unknown} redirectPath
 * @returns {string|null}
 */
export function sanitizeRedirectPath(redirectPath) {
  if (!isString(redirectPath) || !redirectPath.startsWith('/')) {
    return null;
  }

  let parsed;
  try {
    parsed = new URL(redirectPath, window.location.origin);
  } catch {
    return null;
  }

  if (parsed.origin !== window.location.origin) {
    return null;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * vue-router 4 drops the query and hash written inside `{ path }`, so they have to travel as
 * their own fields or a deep link loses them at login.
 * @param {unknown} redirectPath
 * @returns {{ path: string, query: Record<string, unknown>, hash: string }|null}
 */
export function buildRedirectLocation(redirectPath) {
  const safePath = sanitizeRedirectPath(redirectPath);
  if (!safePath) {
    return null;
  }

  const parsed = new URL(safePath, window.location.origin);
  return {
    path: parsed.pathname,
    query: parseQuery(parsed.search),
    hash: parsed.hash
  };
}

/**
 * Query the router guard sends to /login so the user comes back where they were.
 * `fullPath`, not `path`: a deep link's query (the pickup container id) has to survive login.
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {Record<string, string>}
 */
export function buildLoginRedirectQuery(to) {
  return buildLoginQueryWithMcpTransaction(to.query, { redirectPath: to.fullPath });
}

/** Landing paths that mean "no real destination", so the caller falls through to its default. */
export function isLandingPath(path) {
  return path === '/login' || path === '/';
}

/**
 * Manage-appointment login context from query (Vue Router 4 drops undeclared params).
 * @param {Record<string, unknown>} [query]
 * @returns {{
 *   isManageAppointmentLogin: boolean,
 *   appointmentId: string | null,
 *   postLoginRoute: { name: string, params: { appointmentId: string } } | null
 * }}
 */
export function readManageAppointmentFromQuery(query = {}) {
  const appointmentId =
    typeof query.appointmentId === 'string' && query.appointmentId.length > 0
      ? query.appointmentId
      : null;
  const flagged = query.manageAppointment === '1' || query.manageAppointment === 'true';
  const isManageAppointmentLogin = Boolean(flagged && appointmentId);

  return {
    isManageAppointmentLogin,
    appointmentId: isManageAppointmentLogin ? appointmentId : null,
    postLoginRoute: isManageAppointmentLogin
      ? { name: 'appointment.details', params: { appointmentId } }
      : null
  };
}

/**
 * Match the LoginForm carrier redirect logic once the user is already authenticated.
 * @param {{ redirectPath?: unknown, me: object, postLoginRoute?: object | null }} params
 * @returns {Promise<{ path: string } | { name: string } | object>}
 */
export async function resolveAuthenticatedLoginDestination({ redirectPath, me, postLoginRoute }) {
  const destination = buildRedirectLocation(redirectPath);
  if (destination && !isLandingPath(destination.path)) {
    return destination;
  }

  if (postLoginRoute) {
    return postLoginRoute;
  }

  const previewCompany = await companyService.getPreviewCompany();
  if (me?.companyId && me.companyId !== previewCompany?.id) {
    return { name: 'warehouse.select' };
  }

  return { name: 'register.company' };
}

/**
 * @param {{
 *   store: import('vuex').Store,
 *   me: object,
 *   mcpTransaction: string | null,
 *   routeJSON?: string
 * }} params
 * @returns {Promise<boolean>}
 */
export async function tryCompleteOrHopMcpLogin({ store, me, mcpTransaction, routeJSON }) {
  if (!mcpTransaction) {
    return false;
  }

  if (isCarrierUser(me)) {
    return completeMcpPortalLogin({
      mcpTransaction,
      mcpUrl: config.mcp_url
    });
  }

  await store.dispatch('Auth/logInToWarehousePortal', { routeJSON, mcpTransaction });
  return true;
}

/**
 * Skip the login form when a valid session already exists.
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} _from
 * @param {import('vue-router').NavigationGuardNext} next
 */
export async function loginBeforeEnter(to, _from, next) {
  if (to.params?.verificationToken) {
    next();
    return;
  }

  const store = useStore();
  const mcpTransaction = readMcpTransaction(to.query);
  const { isManageAppointmentLogin, appointmentId, postLoginRoute } =
    readManageAppointmentFromQuery(to.query);

  if (await routeHelpers.isUserAuthorized(store)) {
    const me = store.getters['Auth/me'];

    let routeJSON;
    if (isManageAppointmentLogin && appointmentId) {
      routeJSON = JSON.stringify({
        name: 'appointments',
        query: { appointmentId }
      });
    }

    if (await tryCompleteOrHopMcpLogin({ store, me, mcpTransaction, routeJSON })) {
      return;
    }

    if (isCarrierUser(me)) {
      try {
        next(
          withMcpTransactionQuery(
            await resolveAuthenticatedLoginDestination({
              redirectPath: to.query.redirectPath,
              me,
              postLoginRoute
            }),
            mcpTransaction
          )
        );
      } catch {
        // Preview lookup (or destination resolve) failed — keep the user in-app, not on /login.
        next(
          withMcpTransactionQuery(
            { name: me?.companyId ? 'warehouse.select' : 'register.company' },
            mcpTransaction
          )
        );
      }
      return;
    }

    await store.dispatch('Auth/logInToWarehousePortal', routeJSON);
    return;
  }

  next();
}
